import { useState } from "react";
import { DeliveryFragment } from "../../../hasura.graphql";
import ViewMultiPicklist from "../../channels/channel-view-form/view-multi-picklist";
import {
  backlogStatus,
  churnedStatus,
  closedLostStatus,
  greenStatus,
  onHoldStatus,
  progressStatus,
  statusChip,
} from "../../utils";
import ToggleBox from "../../toggle-box";
import ViewDeliveryProducts from "./view-delivery-products";
import ViewDeliveryRegions from "./view-delivery-region";
import { LazyLoadImage } from "react-lazy-load-image-component";
import Timeline from "../../timeline";
import { useGetRedundantChannelNamesQuery } from "../../../hasura.graphql";

const PlayoutDeliveryFieldsForm: React.FC<{
  deliveryData: DeliveryFragment;
}> = ({ deliveryData }) => {
  const [hideColumns, setHideColumns] = useState<Array<string>>([]);

  const handleHideColumns = (tab: string) => {
    setHideColumns((hideColumns) => [...hideColumns, tab]);
  };
  const handleUnhideColumns = (tab: string) => {
    setHideColumns(hideColumns.filter((f: string) => f !== tab));
  };
  const { data: redundantChannelData} = useGetRedundantChannelNamesQuery({
    variables: {
      args: {
        input_channel_id: deliveryData.channel?.id
      },
    },
  });
  const redundantChannels = redundantChannelData?.get_redundant_channel_names[0]?.redundant_playout_channels || [];

  // const timelineItems = [
  //   {
  //     name: "In Progress",
  //     active: true,
  //   },
  //   {
  //     name: "Waiting to go live",
  //     active: deliveryData.delivery_status !== "Progress",
  //   },
  //   {
  //     name: "Active",
  //     active:
  //       deliveryData.delivery_status === "Active" ||
  //       deliveryData.delivery_status === "Churned",
  //   },
  //   {
  //     name: "Churned",
  //     active: deliveryData.delivery_status === "Churned",
  //   },
  // ];

  const filtered_churned_reason = deliveryData.churned_reason
    ? deliveryData.churned_reason.replaceAll(/[()"']/g, "")
    : null;

  const allTimelineItems = [
    {
      name: "Ordering",
      active: true,
      date: deliveryData.ordered_date || null,
      reason: null,
    },
    {
      name: "Provisioning Backlog",
      active: [
        ...backlogStatus,
        ...onHoldStatus,
        ...progressStatus,
        ...greenStatus,
        ...closedLostStatus,
        ...churnedStatus,
      ].includes(deliveryData.delivery_status!),
      date: deliveryData.provisioning_start_date || null,
      reason: null,
    },
    {
      name: "Provisioning",
      active: [
        ...onHoldStatus,
        ...progressStatus,
        ...greenStatus,
        ...closedLostStatus,
        ...churnedStatus,
      ].includes(deliveryData.delivery_status!),
      date: null,
      reason: null,
    },
    {
  name: "Provisioning On Hold",
  active: [
    ...onHoldStatus,
    ...greenStatus,
    ...closedLostStatus,
    ...churnedStatus,
    "Provisioning Blocked",
  ].includes(deliveryData.delivery_status!),
  date: deliveryData.on_hold_date || null,
  reason: (
    (deliveryData.on_hold_reason || deliveryData.blocked_reason) ? (
      <div className="text-left">
        {deliveryData.on_hold_reason && (
          <div className="mt-2">
            <span className="font-bold">On Hold Reason:</span> {deliveryData.on_hold_reason}
          </div>
        )}
        {deliveryData.blocked_reason && (
          <div className="mt-2">
            <span className="font-bold">Blocked Reason:</span> {deliveryData.blocked_reason}
          </div>
        )}
        {deliveryData.blocked_reason_details && (
          <div className="mt-2">
            <span className="font-bold">Blocked Reason Details:</span> {deliveryData.blocked_reason_details}
          </div>
        )}
      </div>
    ) : null
  ) as string | null,
},

    {
      name: "Delivered & Active",
      active: [...greenStatus, ...closedLostStatus, ...churnedStatus].includes(
        deliveryData.delivery_status!
      ),
      date: deliveryData.go_live_date || deliveryData.delivered_date || null,
      reason: null,
    },
    {
      name: "Delivery Closed Lost",
      active: [...closedLostStatus, ...churnedStatus].includes(
        deliveryData.delivery_status!
      ),
      date: null,
      reason: null,
    },
    {
      name: "Suspended",
      active: churnedStatus.includes(deliveryData.delivery_status!),
      date: deliveryData.churned_date || null,
      reason: filtered_churned_reason || null,
    },
  ];

  return (
    <div>
      <div style={{ color: "#4E4E4E", fontSize: "14px", fontWeight: "500" }}>
        <ToggleBox
          header="Playout Details"
          handleHideColumns={handleHideColumns}
          handleUnhideColumns={handleUnhideColumns}
          columns={hideColumns}
        >
          <>
            <div className="grid grid-cols-3 gap-y-8 gap-x-36 pb-8">
              <div>
                <label className="text-gray-400">Playout ID</label>
                <div className="mt-2">{deliveryData?.id || "-"}</div>
              </div>
              <div>
                <label className="text-gray-400">Request Origin</label>
                <div className="mt-2">
                  {deliveryData?.request_origin || "-"}
                </div>
              </div>
              <div>
                <label className="text-gray-400">
                  Delivered using Amagi NOW
                </label>
                <div className="mt-2">
                  {deliveryData?.delivered_using_amagi_now || "-"}
                </div>
              </div>
            </div>
            <>
  <div className="grid grid-cols-3 gap-y-8 gap-x-36 pb-8 border-b mb-4">
    <div>
      <div className="text-base font-semibold mb-3">Delivery for</div>
      <div className="flex">
        {deliveryData.order?.customer.amagi_id && (
          <div className="w-16 h-12">
            <LazyLoadImage
              className="h-full w-full border rounded-lg object-contain bg-lightPrimary"
              src={
                deliveryData.order.customer.logo
                  ? `/logos/${deliveryData.order.customer.logo}`
                  : `https://via.placeholder.com/300x200.png/D3DDFC/1924C1?text=${deliveryData.order?.customer.account_name}`
              }
              alt={deliveryData.order.customer.account_name}
            />
          </div>
        )}
        <div className="flex flex-col">
          <a
            href={`/customers/${deliveryData.order?.customer.amagi_id}/details`}
            target="_blank"
            rel="noreferrer"
            className="hover:text-blue-500 hover:underline"
          >
            {deliveryData.order?.customer.account_name &&
            deliveryData.order?.customer.account_name.length > 20
              ? `${deliveryData.order?.customer.account_name.slice(
                  0,
                  20
                )}...`
              : deliveryData.order?.customer.account_name}
          </a>
          <span className="text-sm text-gray-500">
            {deliveryData.order?.customer.amagi_id}
          </span>
        </div>
      </div>
    </div>
    <div>
     
        </div>
      </div>
          <>
          <div className="grid grid-cols-3 gap-y-8 gap-x-36 pb-8">
              <div>
              <div className="text-base font-semibold mb-3">Channel</div>
      <div className="flex">
        {deliveryData.channel?.id && (
          <div className="w-16 h-12">
            <LazyLoadImage
              className="h-full w-full border rounded-lg object-contain bg-lightPrimary"
              src={
                deliveryData.channel.logo
                  ? `/${deliveryData.channel.logo}`
                  : `https://via.placeholder.com/300x200.png/D3DDFC/1924C1?text=${deliveryData.channel?.name}`
              }
              alt={deliveryData.channel.name}
            />
          </div>
        )}
        <div className="ml-3 flex flex-col">
          <a
            href={`/channels/${
              deliveryData?.channel?.channels_grouped !== null &&
              deliveryData?.channel?.channels_grouped !== "No"
                ? "grouped-channel"
                : deliveryData?.channel?.channel_type
                ? deliveryData?.channel?.channel_type.toLowerCase()
                : "linear"
            }/${deliveryData?.channel?.id}`}
            target="_blank"
            rel="noreferrer"
            className="hover:text-blue-500 hover:underline"
          >
            {deliveryData?.channel?.name || ""}
          </a>
          <span className="text-sm text-gray-500">
            {deliveryData?.channel?.id}
          </span>
        </div>
      </div>
    </div>
    <div>
      <label className="text-base font-semibold">
        Delivery Status
      </label>
      <div
        className={`mt-4 w-max rounded-full px-2 text-xs font-semibold leading-5 ${statusChip(
          deliveryData.delivery_status!
        )}`}
      >
        {deliveryData?.delivery_status || "-"}
        </div>
        </div>
         </div>
            </>
            </>
            <div className="grid grid-cols-3 gap-y-12 gap-x-36 pb-8">
            <div>
            <label className="text-gray-400">Redundant Playouts</label>
                <div className="mt-4">
                  {deliveryData?.redundant_playout || "-"}
                </div>
            </div>
            <div className="flex-col items-center">
              <ViewMultiPicklist
                inlineLabel="Redundant Playout Channels"
                isRedundant={true}
                picklist={redundantChannels || []}
              />
            </div>
            </div>
            <div>
                <label className="text-gray-400">Test Playout</label>
                <div className="mt-2">{deliveryData?.test_playout || "-"}</div>
              </div>
            <div className="my-8">
              <ViewDeliveryRegions delRegions={deliveryData.delivered_region} />
            </div>
            <div className="grid grid-cols-3 gap-y-8 items-start gap-x-36 pb-8 border-b">
              <div style={{ display: "grid", gridColumn: "1" }}>
                <label className="text-gray-400">Channel / Feed Type</label>
                <div className="mt-2">{deliveryData?.feed_type || "-"}</div>
              </div>
              <div style={{ display: "grid", gridColumn: "1" }}>
                <ViewMultiPicklist
                  inlineLabel="Delivery Stream Type"
                  picklist={deliveryData.delivery_stream_type}
                />
              </div>
              <div style={{ display: "grid", gridColumn: "1" }}>
                <ViewMultiPicklist
                  inlineLabel="Audio Languages"
                  picklist={
                    deliveryData.audio_languages
                      ? deliveryData.audio_languages
                      : []
                  }
                />
              </div>
              <div style={{ display: "grid", gridColumn: "1" }}>
                <label className="text-gray-400">Ads Plus</label>
                <div className="mt-2">{deliveryData?.ads_plus || "-"}</div>
              </div>
              <div style={{ display: "grid", gridColumn: "1" }}>
                <label className="text-gray-400">Content Plus</label>
                <div className="mt-2">{deliveryData?.content_plus || "-"}</div>
              </div>
            </div>
            <div className="text-base font-semibold mt-8">
              Delivery Timeline
            </div>
            <div className="w-full mt-20 border-b">
              <div className="mx-20" style={{ height: '200px', width: '1000px'}}>
                <Timeline items={allTimelineItems} />
              </div>
            </div>
            <div className="border-b">
              {/* Inventory Split */}
              {deliveryData.deal_type_for_content_partner ===
                "Inventory Split" && (
                <div className="grid grid-cols-3 gap-y-8 gap-x-36 py-8">
                  <div>
                    <label className="text-gray-400">
                      Deal Split with Content Partner
                    </label>
                    <div className="mt-2">
                      {deliveryData?.deal_split_with_content_partner
                        ? `${deliveryData?.deal_split_with_content_partner}%`
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share Monetised by - Primary
                    </label>
                    <div className="mt-2">
                      {deliveryData?.content_partner_primary
                        ? deliveryData?.content_partner_primary
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share Monetised by - Backfill
                    </label>
                    <div className="mt-2">
                      {deliveryData?.content_partner_backfill
                        ? deliveryData?.content_partner_backfill
                        : "-"}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridColumn: "2" }}>
                    <label className="text-gray-400">
                      Inventory Share - Content Partner Primary Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.inventory_share_content_partner_primary_ad_tag
                        ? deliveryData?.inventory_share_content_partner_primary_ad_tag
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share - Content Partner Backfill Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.inventory_share_content_partner_backfill_ad_tag
                        ? deliveryData?.inventory_share_content_partner_backfill_ad_tag
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Deal Split with Platform
                    </label>
                    <div className="mt-2">
                      {deliveryData?.deal_split_with_platform_partner
                        ? `${deliveryData?.deal_split_with_platform_partner}%`
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share Monetised by - Primary
                    </label>
                    <div className="mt-2">
                      {deliveryData?.platform_partner_primary
                        ? deliveryData?.platform_partner_primary
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share Monetised by - Backfill
                    </label>
                    <div className="mt-2">
                      {deliveryData?.platform_partner_backfill
                        ? deliveryData?.platform_partner_backfill
                        : "-"}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridColumn: "2" }}>
                    <label className="text-gray-400">
                      Inventory Share - Platform Primary Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.inventory_share_platform_primary_ad_tag
                        ? deliveryData?.inventory_share_platform_primary_ad_tag
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share - Platform Backfill Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.inventory_share_platform_backfill_ad_tag
                        ? deliveryData?.inventory_share_platform_backfill_ad_tag
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Deal Split - Amagi Share
                    </label>
                    <div className="mt-2">
                      {deliveryData?.deal_split_amagi_share
                        ? `${deliveryData?.deal_split_amagi_share}%`
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share Monetised by - Primary
                    </label>
                    <div className="mt-2">
                      {deliveryData?.amagi_share_primary
                        ? deliveryData?.amagi_share_primary
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share Monetised by - Backfill
                    </label>
                    <div className="mt-2">
                      {deliveryData?.amagi_share_backfill
                        ? deliveryData?.amagi_share_backfill
                        : "-"}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridColumn: "2" }}>
                    <label className="text-gray-400">
                      Inventory Share - Amagi Primary Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.inventory_share_amagi_primary_ad_tag
                        ? deliveryData?.inventory_share_amagi_primary_ad_tag
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Inventory Share - Amagi Backfill Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.inventory_share_amagi_backfill_ad_tag
                        ? deliveryData?.inventory_share_amagi_backfill_ad_tag
                        : "-"}
                    </div>
                  </div>
                </div>
              )}
              {/* Revenue Share */}
              {deliveryData.deal_type_for_content_partner ===
                "Revenue Share" && (
                <div className="grid grid-cols-3 gap-y-8 gap-x-36 py-8">
                  <div style={{ display: "grid", gridColumn: "2" }}>
                    <label className="text-gray-400">
                      Revenue Share Monetised by - Primary
                    </label>
                    <div className="mt-2">
                      {deliveryData?.revenue_share_monetised_by_primary
                        ? deliveryData?.revenue_share_monetised_by_primary
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Revenue Share Monetised by - Backfill
                    </label>
                    <div className="mt-2">
                      {deliveryData?.revenue_share_monetised_by_backfill
                        ? deliveryData?.revenue_share_monetised_by_backfill
                        : "-"}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridColumn: "2" }}>
                    <label className="text-gray-400">
                      Revenue Share - Primary Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.revenue_share_primary_ad_tag
                        ? deliveryData?.revenue_share_primary_ad_tag
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400">
                      Revenue Share - Backfill Ad Tag
                    </label>
                    <div className="mt-2">
                      {deliveryData?.revenue_share_backfill_ad_tag
                        ? deliveryData?.revenue_share_backfill_ad_tag
                        : "-"}
                    </div>
                  </div>
                </div>
              )} 
            </div>
            <div className="my-8">
              <ViewDeliveryProducts
                products={deliveryData.product_purchased_for_this_delivery}
              />
            </div>
          </>
        </ToggleBox>
        <ToggleBox
          header="Meta"
          handleHideColumns={handleHideColumns}
          handleUnhideColumns={handleUnhideColumns}
          columns={hideColumns}
        >
          <div className="grid grid-cols-3 gap-y-8 gap-x-36">
            <div style={{ display: "grid", gridColumn: "1" }}>
              <label className="text-gray-400">Order ID</label>
              <div className="mt-2">
                {deliveryData?.salesforce_order_id || "-"}
              </div>
            </div>
            <div>
              <label className="text-gray-400">Order Product ID</label>
              <div className="mt-2">
                {deliveryData?.salesforce_order_product_id || "-"}
              </div>
            </div>
            <div>
              <label className="text-gray-400">Order Item ID</label>
              <div className="mt-2">
                {deliveryData?.salesforce_order_item_id || "-"}
              </div>
            </div>
          </div>
        </ToggleBox>
      </div>
    </div>
  );
};

export default PlayoutDeliveryFieldsForm;
